import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {DataSourceKind, type DataSource} from "@vesta/vos-types/modules/workspaceLevel/workspaceUiConfigs/domain/WorkspaceConfig";

/** Pages reference builtin data sources by `DataSourceKind` UUID. */
export const BUILTIN_DATA_SOURCES = {
    [DataSourceKind.Event]: {
        kind: DataSourceKind.Event,
        displayName: "Event",
        description: null,
    },
    [DataSourceKind.EventWithEventContainer]: {
        kind: DataSourceKind.EventWithEventContainer,
        displayName: "Event with event container",
        description: null,
    },
    [DataSourceKind.EventContainerWithEvent]: {
        kind: DataSourceKind.EventContainerWithEvent,
        displayName: "Event container with events",
        description: null,
    },
    [DataSourceKind.AgentViewDayPlan]: {
        kind: DataSourceKind.AgentViewDayPlan,
        displayName: "Agent view day plan",
        description: null,
    },
    [DataSourceKind.DuluxAgentViewDayPlan]: {
        kind: DataSourceKind.DuluxAgentViewDayPlan,
        displayName: "Dulux agent view day plan",
        description: null,
    },
    [DataSourceKind.TuffexProduct]: {
        kind: DataSourceKind.TuffexProduct,
        displayName: "Tuffex list",
        description: null,
    },
} as const satisfies Record<DataSourceKind, DataSource>;

export function getBuiltinDataSource(dataSourceId: Uuid): DataSource | undefined {
    if (dataSourceId in BUILTIN_DATA_SOURCES) {
        return BUILTIN_DATA_SOURCES[dataSourceId as DataSourceKind];
    }
    return undefined;
}

/** Runtime family used by Neptune list helpers. */
export type DataSourceRuntimeFamily =
    | "event"
    | "eventWithEventContainer"
    | "eventContainer"
    | "agentViewDayPlan"
    | "duluxAgentViewDayPlan"
    | "tuffexProduct";

export function dataSourceKindToRuntimeFamily(kind: DataSourceKind): DataSourceRuntimeFamily {
    switch (kind) {
        case DataSourceKind.Event:
            return "event";
        case DataSourceKind.EventWithEventContainer:
            return "eventWithEventContainer";
        case DataSourceKind.EventContainerWithEvent:
            return "eventContainer";
        case DataSourceKind.AgentViewDayPlan:
            return "agentViewDayPlan";
        case DataSourceKind.DuluxAgentViewDayPlan:
            return "duluxAgentViewDayPlan";
        case DataSourceKind.TuffexProduct:
            return "tuffexProduct";
    }
}

/** Nested container columns need `eventContainer.` only on event-primary list APIs. */
export function dataSourceUsesEventContainerColumnPrefix(kind: DataSourceKind): boolean {
    return kind === DataSourceKind.EventWithEventContainer;
}

export function createBuiltinDataSourcesRecord(): Record<Uuid, DataSource> {
    const record: Record<Uuid, DataSource> = {};
    for (const kind of Object.values(DataSourceKind)) {
        record[kind as Uuid] = BUILTIN_DATA_SOURCES[kind];
    }
    return record;
}
